import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { productsQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/shop")({
  head: () => ({
    meta: [
      { title: "Online Shop | Argentinian.Soccer" },
      { name: "description", content: "Argentina jerseys, accessories, books, tickets and streaming — all in the Argentinian.Soccer shop." },
      { property: "og:title", content: "Online Shop | Argentinian.Soccer" },
      { property: "og:url", content: "/shop" },
    ],
    links: [{ rel: "canonical", href: "/shop" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQuery()),
  component: ShopPage,
});

const SECTIONS: { key: string; label: string; subs?: string[] }[] = [
  { key: "most_sold", label: "Most Sold" },
  { key: "clothes", label: "Clothes", subs: ["t_shirts", "football_products", "pants", "accessories"] },
  { key: "books", label: "Books" },
  { key: "tickets", label: "Tickets" },
  { key: "streaming", label: "Streaming" },
  { key: "merchandise", label: "Merchendise" },
];

const SUB_LABELS: Record<string, string> = {
  t_shirts: "T-Shirts",
  football_products: "Football Products",
  pants: "Pants",
  accessories: "Accessories",
};

function ShopPage() {
  const { data: products } = useSuspenseQuery(productsQuery());
  const mostSold = [...products].sort((a, b) => (b.sold_count ?? 0) - (a.sold_count ?? 0)).slice(0, 4);

  return (
    <>
      <PageHero
        eyebrow="Shop"
        title="Online Shop"
        description="Curated gear for the global Argentina supporter community."
      />

      {SECTIONS.map((s) => {
        const items =
          s.key === "most_sold"
            ? mostSold
            : products.filter((p) => p.category === s.key);
        return (
          <Section key={s.key} id={s.key} eyebrow="Shop" title={s.label}>
            {s.subs && (
              <div className="mb-5 flex flex-wrap gap-2">
                {s.subs.map((sub) => (
                  <span key={sub} className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
                    {SUB_LABELS[sub]}
                  </span>
                ))}
              </div>
            )}
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing here yet.</p>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {items.map((p) => (
                  <div key={p.id} className="flex flex-col gap-2 rounded-lg border border-border bg-card p-5">
                    <p className="eyebrow">{p.subcategory ? SUB_LABELS[p.subcategory] ?? p.subcategory : p.category}</p>
                    <h3 className="font-display text-lg font-bold text-navy">{p.name}</h3>
                    {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
                    <p className="mt-auto font-display text-2xl font-bold text-primary">${p.price}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        );
      })}
    </>
  );
}
